import React, { useState, useMemo, useCallback } from 'react';
import { useData } from 'vike-react/useData';
import { MapPin, CheckCircle, ChevronDown, MessageCircle } from 'lucide-react';
import HeaderSSR from '../../components/HeaderSSR';
import FooterSSR from '../../components/FooterSSR';
import { agentDirectoryContent } from '../../../data/agentDirectoryContent';
import type { AgentDirectoryData } from './+data';

// ========== TYPES ==========
interface Agent {
  id: string;
  full_name: string;
  slug: string;
  avatar_url: string | null;
  bio: string | null;
  agency_name: string | null;
  is_independent: boolean;
  location: string | null;
  is_verified: boolean;
}

// ========== HELPERS ==========
function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

function getAvatarColor(name: string): string {
  const colors = [
    'bg-emerald-600',
    'bg-blue-600',
    'bg-violet-600',
    'bg-amber-600',
    'bg-rose-600',
    'bg-teal-600',
    'bg-indigo-600',
    'bg-orange-600',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

function truncateBio(bio: string | null, maxLength: number = 150): string {
  if (!bio) return '';
  if (bio.length <= maxLength) return bio;
  return bio.slice(0, maxLength).trimEnd() + '...';
}

// ========== AGENT CARD ==========
function AgentCard({ agent, priority }: { agent: Agent; priority?: boolean }) {
  const { cardLabels } = agentDirectoryContent;

  return (
    <article className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-md transition-shadow duration-200">
      <div className="flex items-start gap-4">
        {/* Avatar */}
        {agent.avatar_url ? (
          <img
            src={agent.avatar_url}
            alt={agent.full_name}
            width={80}
            height={80}
            className="w-20 h-20 rounded-full object-cover flex-shrink-0"
            loading={priority ? 'eager' : 'lazy'}
          />
        ) : (
          <div
            className={`w-20 h-20 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xl font-semibold ${getAvatarColor(agent.full_name)}`}
          >
            {getInitials(agent.full_name)}
          </div>
        )}

        <div className="flex-1 min-w-0">
          {/* Name + Verified */}
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {agent.full_name}
            </h3>
            {agent.is_verified && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full whitespace-nowrap">
                <CheckCircle className="w-3 h-3" />
                {cardLabels.verified}
              </span>
            )}
          </div>

          {/* Agency */}
          <p className="text-gray-600 text-sm mt-0.5">
            {agent.agency_name || cardLabels.independent}
          </p>

          {/* Location */}
          {agent.location && (
            <p className="text-gray-500 text-sm flex items-center gap-1 mt-1">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              {agent.location}
            </p>
          )}
        </div>
      </div>

      {/* Bio */}
      {agent.bio && (
        <p className="text-gray-600 text-sm mt-4 leading-relaxed">
          {truncateBio(agent.bio)}
        </p>
      )}

      {/* CTA — contact happens on the agent profile page (TECH-016: no phone on
          list cards). View Profile routes there for Call/WhatsApp. */}
      <div className="flex items-center gap-3 mt-4">
        <a
          href={`/agent/${agent.slug}`}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-xl hover:bg-green-700 transition-colors"
        >
          {cardLabels.viewProfile} &rarr;
        </a>
      </div>
    </article>
  );
}

// ========== FAQ ITEM ==========
function FAQItem({
  question,
  answer,
  isOpen,
  onToggle,
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-5 text-left"
        aria-expanded={isOpen}
      >
        <span className="text-base font-medium text-gray-900 pr-4">
          {question}
        </span>
        <ChevronDown
          className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className="pb-5 pr-8">
          <p className="text-gray-600 text-sm leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

// ========== MAIN PAGE ==========
export default function AgentsDirectoryPage() {
  const { agents } = useData<AgentDirectoryData>();
  const { hero, sortOptions, emptyState, bottomCta, faq, agentCta, featuredOrder } =
    agentDirectoryContent;

  const [sortBy, setSortBy] = useState('featured');
  const [openFaqItems, setOpenFaqItems] = useState<Set<number>>(new Set());

  const toggleFaq = useCallback((index: number) => {
    setOpenFaqItems((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const sortedAgents = useMemo(() => {
    const list = [...agents];

    switch (sortBy) {
      case 'featured': {
        return list.sort((a, b) => {
          const aIdx = featuredOrder.indexOf(a.slug);
          const bIdx = featuredOrder.indexOf(b.slug);
          // Agents in the featured list come first in order
          if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
          if (aIdx !== -1) return -1;
          if (bIdx !== -1) return 1;
          // Non-featured agents sorted alphabetically
          return a.full_name.localeCompare(b.full_name);
        });
      }
      case 'name-asc':
        return list.sort((a, b) => a.full_name.localeCompare(b.full_name));
      case 'name-desc':
        return list.sort((a, b) => b.full_name.localeCompare(a.full_name));
      default:
        return list;
    }
  }, [agents, sortBy, featuredOrder]);

  const whatsappCtaUrl = `https://wa.me/${bottomCta.ctaWhatsApp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent("Hi, I'm looking for help finding a real estate agent on Afriqahome.")}`;
  const emptyWhatsappUrl = `https://wa.me/${emptyState.ctaWhatsApp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent("Hi, I'm looking for a real estate agent on Afriqahome.")}`;

  return (
    <div className="min-h-screen bg-brand-bg">
      <HeaderSSR />

      {/* Hero Section */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 text-center">
          <h1 className="text-h1 md:text-display text-gray-900">
            {hero.title}
          </h1>
          <p className="mt-3 text-lg text-gray-600 max-w-2xl mx-auto">
            {hero.subtitle}
          </p>
          <p className="mt-4 text-sm text-gray-500">
            {hero.trustLine}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {agents.length > 0 ? (
          <>
            {/* Sort Controls */}
            <div className="flex items-center justify-end mb-8">
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none bg-white border border-gray-200 rounded-lg px-4 py-2 pr-10 text-sm text-gray-700 cursor-pointer hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  {sortOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Agent Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sortedAgents.map((agent, index) => (
                <AgentCard
                  key={agent.id}
                  agent={agent}
                  priority={index < 6}
                />
              ))}
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="text-center py-16">
            <h2 className="text-h2 text-gray-900">{emptyState.title}</h2>
            <p className="mt-3 text-gray-600 max-w-lg mx-auto">
              {emptyState.description}
            </p>
            <a
              href={emptyWhatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              {emptyState.ctaText}
            </a>
          </div>
        )}

        {/* Bottom CTA Section */}
        {agents.length > 0 && (
          <section className="mt-16 bg-white rounded-2xl border border-gray-100 p-8 md:p-12 text-center">
            <h2 className="text-h2 text-gray-900">{bottomCta.title}</h2>
            <p className="mt-3 text-gray-600 max-w-xl mx-auto">
              {bottomCta.description}
            </p>
            <a
              href={whatsappCtaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              {bottomCta.ctaText}
            </a>
          </section>
        )}

        {/* FAQ Section */}
        <section className="mt-16">
          <h2 className="text-h2 text-gray-900 text-center mb-8">
            {faq.title}
          </h2>
          <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-gray-100 px-6 md:px-8">
            {faq.items.map((item, index) => (
              <FAQItem
                key={index}
                question={item.question}
                answer={item.answer}
                isOpen={openFaqItems.has(index)}
                onToggle={() => toggleFaq(index)}
              />
            ))}
          </div>
        </section>

        {/* Agent CTA Section */}
        <section className="mt-16 bg-brand-primary rounded-2xl p-8 md:p-12 text-center">
          <h2 className="text-h2 text-white">{agentCta.title}</h2>
          <p className="mt-3 text-green-100 max-w-xl mx-auto">
            {agentCta.description}
          </p>
          <a
            href={agentCta.ctaLink}
            className="mt-6 inline-flex items-center px-6 py-3 bg-white text-brand-primary font-medium rounded-xl hover:bg-gray-50 transition-colors"
          >
            {agentCta.ctaText}
          </a>
        </section>
      </div>

      <FooterSSR />
    </div>
  );
}
